import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Chip,
  Divider,
  Snackbar,
  Alert,
  Tooltip,
  Grid,
  CircularProgress,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Search,
  Close,
  CheckCircleOutline,
  WarningAmberRounded,
  Diamond,
  AutoAwesome,
} from "@mui/icons-material";
import axios from "axios"; // or your API client

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

// ─── MOCK DATA for other entities (HSN, GST, Floors, Barcode Templates) (These can be replaced with real API calls later) ───
const MOCK_HSN = [
  { code: "711319", label: "711319 | MIX GOLD ORNAMENTS" },
  { code: "711311", label: "711311 | SILVER ORNAMENTS" },
  { code: "710813", label: "710813 | GOLD BULLION" },
];

const MOCK_GST = [
  { id: 1, label: "GST@3" },
  { id: 2, label: "GST@5" },
  { id: 3, label: "GST@18" },
];

const MOCK_FLOORS = [
  { id: 1, name: "Ground Floor" },
  { id: 2, name: "First Floor" },
  { id: 3, name: "Basement" },
];

const MOCK_BARCODE_TEMPLATES = [
  { id: 1, name: "Template A" },
  { id: 2, name: "Template B" },
];

// MOCK_ITEM_TYPES remains the same (or you'll replace with real API later)
const MOCK_ITEM_TYPES = [
  {
    item_type_id: 1,
    name: "BANGLE",
    short_code: "BN",
    default_pcs: 2,
    display_order: 1,
    is_active: true,
    metals: [
      {
        id: 1,
        metal_type_id: 1,
        metalName: "GOLD",
        hsn_code: "711319",
        gst_type: 1,
        mc_type: "GROSS WT",
        rfid_flag: true,
        quick_sell: false,
        size_wise_tag: false,
        others_stock: false,
        old_metal: false,
        tag_counter: 1,
        stock_percent: 5,
        reorder_percent: 4,
        abc_a_percent: 30,
        abc_b_percent: 40,
        abc_c_percent: 20,
        abc_d_percent: 10,
        group_name: "",
        group_prefix: "",
        default_floor_id: 1,
        barcode_template_id: 1,
        opening_stock: 0,
        opening_rate: 0,
        gross_percent: 100,
        net_percent: 100,
      },
    ],
    incentives: [
      {
        id: 1,
        metal_type_id: 1,
        incentive_type: "PER_GM",
        incentive_value: 50,
      },
    ],
  },
  {
    item_type_id: 2,
    name: "CHAIN",
    short_code: "CH",
    default_pcs: 1,
    display_order: 2,
    is_active: true,
    metals: [],
    incentives: [],
  },
];

const EMPTY_ITEM = {
  name: "",
  short_code: "",
  default_pcs: 1,
  display_order: 0,
};

const EMPTY_MAPPING = {
  metal_type_id: "",
  hsn_code: "",
  gst_type: "",
  mc_type: "GROSS WT",
  rfid_flag: true,
  quick_sell: false,
  size_wise_tag: false,
  others_stock: false,
  old_metal: false,
  tag_counter: 1,
  stock_percent: "",
  reorder_percent: "",
  abc_a_percent: "",
  abc_b_percent: "",
  abc_c_percent: "",
  abc_d_percent: "",
  group_name: "",
  group_prefix: "",
  default_floor_id: "",
  barcode_template_id: "",
  opening_stock: 0,
  opening_rate: 0,
  opening_stock2: 0,
  opening_rate2: 0,
  gross_percent: 100,
  net_percent: 100,
};

const EMPTY_INCENTIVE = {
  metal_type_id: "",
  incentive_type: "PER_GM",
  incentive_value: "",
};

// ─── SNACKBAR HELPER ─────────────────────────────────────────────────────────
function useSnack() {
  const [snack, setSnack] = useState({
    open: false,
    msg: "",
    severity: "success",
  });
  const show = useCallback(
    (msg, severity = "success") => setSnack({ open: true, msg, severity }),
    [],
  );
  const hide = useCallback(() => setSnack((s) => ({ ...s, open: false })), []);
  return { snack, show, hide };
}

// ─── STAT CHIP ────────────────────────────────────────────────────────────────
function StatChip({ count, label, color }) {
  return (
    <Box sx={{ textAlign: "center", px: 2 }}>
      <Typography variant="h4" fontWeight={700} color={color} lineHeight={1}>
        {count}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

// ─── ABC PERCENT FIELDS ───────────────────────────────────────────────────────
function AbcFields({ mapping, setMapping }) {
  const letters = ["a", "b", "c", "d"];
  const colors = { a: "#10b981", b: "#3b82f6", c: "#f59e0b", d: "#ef4444" };
  return (
    <Grid container spacing={1.5}>
      {letters.map((l) => (
        <Grid item xs={3} key={l}>
          <TextField
            fullWidth
            size="small"
            label={`${l.toUpperCase()} %`}
            type="number"
            value={mapping[`abc_${l}_percent`]}
            onChange={(e) =>
              setMapping((p) => ({
                ...p,
                [`abc_${l}_percent`]: e.target.value,
              }))
            }
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
              sx: { "& input": { color: colors[l], fontWeight: 600 } },
            }}
          />
        </Grid>
      ))}
    </Grid>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const ItemTypeMaster = ({ storeId: propStoreId }) => {
  const loggedUser = JSON.parse(localStorage.getItem("CapacitorStorage.user"));
  const store_id = propStoreId ?? loggedUser?.store_id;

  const theme = useTheme();
  const { snack, show, hide } = useSnack();

  const [itemTypes, setItemTypes] = useState(MOCK_ITEM_TYPES);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  // Metal types from API
  const [metalTypes, setMetalTypes] = useState([]);
  const [loadingMetals, setLoadingMetals] = useState(false);

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);

  // Form data
  const [itemForm, setItemForm] = useState(EMPTY_ITEM);
  const [mappings, setMappings] = useState([]);
  const [incentives, setIncentives] = useState([]);

  // Sub-dialog: metal mapping
  const [metalDialogOpen, setMetalDialogOpen] = useState(false);
  const [metalEditId, setMetalEditId] = useState(null);
  const [mappingForm, setMappingForm] = useState(EMPTY_MAPPING);
  const [metalTabIdx, setMetalTabIdx] = useState(0);

  // Sub-dialog: incentive
  const [incDialogOpen, setIncDialogOpen] = useState(false);
  const [incEditId, setIncEditId] = useState(null);
  const [incForm, setIncForm] = useState(EMPTY_INCENTIVE);

  // Delete confirm
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    type: "",
    id: null,
    label: "",
  });

  // ── Fetch metal types from API ──
  useEffect(() => {
    if (store_id === undefined || store_id === null) return;
    const fetchMetalTypes = async () => {
      setLoadingMetals(true);
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/jewel-master/metal-type-master/store/${store_id}`,
          { params: { is_active: true } },
        );
        if (response.data.success) {
          setMetalTypes(response.data.data);
        } else {
          show(
            response.data.message || "Failed to fetch metal types.",
            "error",
          );
        }
      } catch (error) {
        console.error("Error fetching metal types:", error);
        show("Could not load metal types. Please try again.", "error");
      } finally {
        setLoadingMetals(false);
      }
    };
    fetchMetalTypes();
  }, [store_id, show]);

  // ── Fetch item types from API ──
  const fetchItemTypes = useCallback(async () => {
    if (store_id === undefined || store_id === null) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/jewel-master/item-type-master/store/${store_id}`,
        { params: { is_active: true } },
      );
      if (response.data.success) {
        setItemTypes(response.data.data);
      } else {
        show(
          response.data.errorMessage || "Failed to fetch item types.",
          "error",
        );
        setItemTypes(MOCK_ITEM_TYPES);
      }
    } catch (error) {
      console.error("Error fetching item types:", error);
      show("Could not load item types. Using mock data.", "warning");
      setItemTypes(MOCK_ITEM_TYPES);
    } finally {
      setLoading(false);
    }
  }, [store_id, show]);

  useEffect(() => {
    fetchItemTypes();
  }, [fetchItemTypes]);

  useEffect(() => {
    fetchItemTypes();
  }, [fetchItemTypes]);

  const filtered = itemTypes.filter(
    (it) =>
      it.name.toLowerCase().includes(search.toLowerCase()) ||
      it.short_code.toLowerCase().includes(search.toLowerCase()),
  );

  // ── Open create/edit dialog ──
  const openCreate = () => {
    setEditMode(false);
    setSelectedItem(null);
    setItemForm(EMPTY_ITEM);
    setMappings([]);
    setIncentives([]);
    setTabIndex(0);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditMode(true);
    setSelectedItem(item);
    setItemForm({
      name: item.name,
      short_code: item.short_code,
      default_pcs: item.default_pcs,
      display_order: item.display_order,
    });
    setMappings(item.metals || []);
    setIncentives(item.incentives || []);
    setTabIndex(0);
    setDialogOpen(true);
  };

  // ── Save item type (create/update) ──
  const saveItemType = async () => {
    if (!itemForm.name.trim())
      return show("Item type name is required.", "error");
    if (!itemForm.short_code.trim())
      return show("Short code is required.", "error");

    const upper = itemForm.short_code.trim().toUpperCase();
    const payload = {
      store_id,
      name: itemForm.name,
      short_code: upper,
      default_pcs: itemForm.default_pcs,
      display_order: itemForm.display_order,
      is_active: true,
      created_by: loggedUser?.id,
      updated_by: editMode ? loggedUser?.id : undefined,
      metals: mappings,
      incentives,
    };

    try {
      let response;
      if (editMode && selectedItem) {
        response = await axios.put(
          `${API_BASE_URL}/api/jewel-master/item-type-master/${selectedItem.item_type_id}`,
          payload,
        );
      } else {
        response = await axios.post(
          `${API_BASE_URL}/api/jewel-master/item-type-master`,
          payload,
        );
      }

      if (response.data.success) {
        show(response.data.message || "Item type saved successfully.");
        setDialogOpen(false);
        fetchItemTypes(); // refresh list
      } else {
        show(response.data.errorMessage || "Operation failed.", "error");
      }
    } catch (error) {
      console.error("Save item type error:", error);
      show("Network error. Please try again.", "error");
    }
  };

  // ── Metal mapping dialog ──
  const openAddMetal = () => {
    setMetalEditId(null);
    setMappingForm(EMPTY_MAPPING);
    setMetalTabIdx(0);
    setMetalDialogOpen(true);
  };

  const openEditMetal = (m) => {
    setMetalEditId(m.id);
    setMappingForm({ ...EMPTY_MAPPING, ...m });
    setMetalTabIdx(0);
    setMetalDialogOpen(true);
  };

  const saveMapping = () => {
    if (!mappingForm.metal_type_id)
      return show("Please select a metal type.", "error");
    if (!mappingForm.hsn_code) return show("HSN Code is required.", "error");
    if (!mappingForm.gst_type) return show("GST Type is required.", "error");

    const sp = Number(mappingForm.stock_percent);
    const rp = Number(mappingForm.reorder_percent);
    if (sp > 0 && rp >= sp)
      return show("Reorder % must be less than Stock %.", "error");

    // Find metal name from fetched metalTypes
    const metalName =
      metalTypes.find((m) => m.id === Number(mappingForm.metal_type_id))
        ?.name || "";

    if (metalEditId) {
      setMappings((prev) =>
        prev.map((m) =>
          m.id === metalEditId
            ? { ...mappingForm, id: metalEditId, metalName }
            : m,
        ),
      );
      show("Metal mapping updated.");
    } else {
      const dup = mappings.find(
        (m) => Number(m.metal_type_id) === Number(mappingForm.metal_type_id),
      );
      if (dup)
        return show("This metal is already mapped to this item type.", "error");
      setMappings((prev) => [
        ...prev,
        { ...mappingForm, id: Date.now(), metalName },
      ]);
      show("Metal mapping added.");
    }
    setMetalDialogOpen(false);
  };

  const confirmDeleteMapping = (m) =>
    setDeleteConfirm({
      open: true,
      type: "mapping",
      id: m.id,
      label: `${m.metalName} mapping`,
    });

  const confirmDeleteIncentive = (i) =>
    setDeleteConfirm({
      open: true,
      type: "incentive",
      id: i.id,
      label: `${i.incentive_type} incentive`,
    });

  // ── Delete item type ──
  const confirmDeleteItemType = (it) => {
    setDeleteConfirm({
      open: true,
      type: "itemtype",
      id: it.item_type_id,
      label: it.name,
    });
  };

  const executeDelete = async () => {
    const { type, id } = deleteConfirm;
    if (type === "itemtype") {
      try {
        const response = await axios.delete(
          `${API_BASE_URL}/api/jewel-master/item-type-master/${id}`,
          { params: { store_id } },
        );
        if (response.data.success) {
          show(response.data.message || "Item type deleted.");
          fetchItemTypes();
        } else {
          show(response.data.errorMessage || "Delete failed.", "error");
        }
      } catch (error) {
        console.error("Delete error:", error);
        show("Network error.", "error");
      }
    } else if (type === "mapping") {
      // mapping removal is done locally (no API call)
      setMappings((prev) => prev.filter((m) => m.id !== id));
      show("Metal mapping removed (local change). Save item type to persist.");
    } else if (type === "incentive") {
      setIncentives((prev) => prev.filter((i) => i.id !== id));
      show("Incentive removed (local change). Save item type to persist.");
    }
    setDeleteConfirm({ open: false, type: "", id: null, label: "" });
  };

  // ── Incentive dialog ──
  const openAddIncentive = () => {
    setIncEditId(null);
    setIncForm(EMPTY_INCENTIVE);
    setIncDialogOpen(true);
  };

  const openEditIncentive = (i) => {
    setIncEditId(i.id);
    setIncForm({
      metal_type_id: i.metal_type_id,
      incentive_type: i.incentive_type,
      incentive_value: i.incentive_value,
    });
    setIncDialogOpen(true);
  };

  const saveIncentive = () => {
    if (!incForm.incentive_type)
      return show("Incentive type is required.", "error");
    if (incForm.incentive_value === "" || incForm.incentive_value === undefined)
      return show("Incentive value is required.", "error");

    const metalName = incForm.metal_type_id
      ? metalTypes.find((m) => m.id === Number(incForm.metal_type_id))?.name ||
        ""
      : "All Metals";

    if (incEditId) {
      setIncentives((prev) =>
        prev.map((i) =>
          i.id === incEditId ? { ...incForm, id: incEditId, metalName } : i,
        ),
      );
      show("Incentive updated.");
    } else {
      setIncentives((prev) => [
        ...prev,
        { ...incForm, id: Date.now(), metalName },
      ]);
      show("Incentive added.");
    }
    setIncDialogOpen(false);
  };

  const MC_TYPES = ["NET WT", "GROSS WT", "TOTAL WT"];

  // ─── STATS ────────────────────────────────────────────────────────────────
  const totalActive = itemTypes.filter((i) => i.is_active).length;
  const withMetal = itemTypes.filter((i) => i.metals?.length > 0).length;
  const withIncentives = itemTypes.filter(
    (i) => i.incentives?.length > 0,
  ).length;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: "auto" }}>
      {/* ── Header ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              background: "linear-gradient(135deg, #f59e0b, #d97706)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Diamond sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} lineHeight={1}>
              Item Type Master
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Manage product categories per metal
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={openCreate}
          sx={{
            background: "linear-gradient(135deg, #f59e0b, #d97706)",
            color: "#fff",
            fontWeight: 600,
            borderRadius: 2,
            boxShadow: "0 4px 14px rgba(245,158,11,0.4)",
            "&:hover": {
              background: "linear-gradient(135deg, #d97706, #b45309)",
            },
          }}
        >
          Add Item Type
        </Button>
      </Box>

      {/* ── Stats ── */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <StatChip count={totalActive} label="Active Types" color="#f59e0b" />
        <Divider orientation="vertical" flexItem />
        <StatChip count={withMetal} label="With Metal Maps" color="#3b82f6" />
        <Divider orientation="vertical" flexItem />
        <StatChip
          count={withIncentives}
          label="With Incentives"
          color="#10b981"
        />
      </Paper>

      {/* ── Search ── */}
      <TextField
        placeholder="Search by name or short code..."
        size="small"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, width: { xs: "100%", sm: 320 } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {/* ── Table ── */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider" }}
      >
        <Table size="small">
          <TableHead>
            <TableRow
              sx={{
                "& th": {
                  fontWeight: 700,
                  fontSize: "0.78rem",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                },
              }}
            >
              <TableCell>#</TableCell>
              <TableCell>Item Type</TableCell>
              <TableCell>Short Code</TableCell>
              <TableCell align="center">Def. Pcs</TableCell>
              <TableCell align="center">Metals</TableCell>
              <TableCell align="center">Incentives</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  align="center"
                  sx={{ py: 5, color: "text.secondary" }}
                >
                  <AutoAwesome
                    sx={{
                      mb: 1,
                      opacity: 0.3,
                      fontSize: 36,
                      display: "block",
                      mx: "auto",
                    }}
                  />
                  No item types found
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((it, idx) => (
                <TableRow
                  key={it.item_type_id}
                  hover
                  sx={{ "&:last-child td": { border: 0 }, cursor: "pointer" }}
                  onClick={() => openEdit(it)}
                >
                  <TableCell
                    sx={{ color: "text.secondary", fontSize: "0.8rem" }}
                  >
                    {idx + 1}
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600} fontSize="0.9rem">
                      {it.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={it.short_code}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        bgcolor: alpha("#f59e0b", 0.12),
                        color: "#b45309",
                        fontSize: "0.78rem",
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">{it.default_pcs}</TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        justifyContent: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      {it.metals?.length > 0 ? (
                        it.metals.map((m) => (
                          <Chip
                            key={m.id}
                            label={m.metalName}
                            size="small"
                            sx={{
                              fontSize: "0.7rem",
                              bgcolor: alpha("#3b82f6", 0.1),
                              color: "#1d4ed8",
                            }}
                          />
                        ))
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          None
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    {it.incentives?.length > 0 ? (
                      <Chip
                        label={`${it.incentives.length} set`}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="caption" color="text.disabled">
                        None
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell
                    align="center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(it)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => confirmDeleteItemType(it)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ═══════════════════════════════════════════════════
          MAIN CREATE / EDIT DIALOG
      ═══════════════════════════════════════════════════ */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Diamond sx={{ color: "#f59e0b" }} />
            <Typography fontWeight={700}>
              {editMode ? `Edit — ${selectedItem?.name}` : "New Item Type"}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setDialogOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>

        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          sx={{ px: 3, borderBottom: "1px solid", borderColor: "divider" }}
        >
          <Tab label="Item Type" />
          <Tab
            label={`Other Details ${
              mappings.length > 0 ? `(${mappings.length})` : ""
            }`}
          />
          <Tab
            label={`Incentives ${
              incentives.length > 0 ? `(${incentives.length})` : ""
            }`}
          />
        </Tabs>

        <DialogContent sx={{ pt: 3 }}>
          {/* ── Tab 0: Item Type ── */}
          {tabIndex === 0 && (
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Item Type Name *"
                  size="small"
                  value={itemForm.name}
                  onChange={(e) =>
                    setItemForm((p) => ({
                      ...p,
                      name: e.target.value.toUpperCase(),
                    }))
                  }
                  placeholder="e.g. BANGLE"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Short Code *"
                  size="small"
                  value={itemForm.short_code}
                  onChange={(e) =>
                    setItemForm((p) => ({
                      ...p,
                      short_code: e.target.value.toUpperCase(),
                    }))
                  }
                  placeholder="e.g. BN"
                  inputProps={{ maxLength: 20 }}
                  helperText="Unique across system"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Default Pieces"
                  size="small"
                  type="number"
                  value={itemForm.default_pcs}
                  onChange={(e) =>
                    setItemForm((p) => ({
                      ...p,
                      default_pcs: Number(e.target.value),
                    }))
                  }
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Display Order"
                  size="small"
                  type="number"
                  value={itemForm.display_order}
                  onChange={(e) =>
                    setItemForm((p) => ({
                      ...p,
                      display_order: Number(e.target.value),
                    }))
                  }
                />
              </Grid>
              {/* Preview of auto-generated codes - now using real metalTypes */}
              {itemForm.short_code && metalTypes.length > 0 && (
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha("#f59e0b", 0.06),
                      border: "1px dashed #f59e0b",
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="#b45309"
                      display="block"
                      mb={1}
                    >
                      Auto-generated Metal Codes (Short Code strategy)
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {metalTypes.map((m) => (
                        <Chip
                          key={m.id}
                          label={`${m.name[0]}-${itemForm.short_code}`}
                          size="small"
                          sx={{ fontWeight: 700, fontSize: "0.75rem" }}
                        />
                      ))}
                    </Box>
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}

          {/* ── Tab 1: Other Details (Metal Mapping) ── */}
          {tabIndex === 1 && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  fontWeight={600}
                  color="text.secondary"
                  fontSize="0.85rem"
                >
                  Metal Mappings
                </Typography>
                <Button
                  size="small"
                  startIcon={<Add />}
                  variant="outlined"
                  onClick={openAddMetal}
                  sx={{ borderRadius: 2 }}
                >
                  Add Metal
                </Button>
              </Box>

              {mappings.length === 0 ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    textAlign: "center",
                    borderRadius: 2,
                    border: "1px dashed",
                    borderColor: "divider",
                  }}
                >
                  <Typography color="text.disabled" fontSize="0.85rem">
                    No metals mapped yet. Click "Add Metal" to configure.
                  </Typography>
                </Paper>
              ) : (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  {mappings.map((m) => (
                    <Paper
                      key={m.id}
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <Box>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                              mb: 0.5,
                            }}
                          >
                            <Chip
                              label={
                                m.metalName ||
                                metalTypes.find(
                                  (mt) => mt.id === Number(m.metal_type_id),
                                )?.name
                              }
                              size="small"
                              sx={{
                                fontWeight: 700,
                                bgcolor: alpha("#3b82f6", 0.1),
                                color: "#1d4ed8",
                              }}
                            />
                            <Chip
                              label={m.mc_type}
                              size="small"
                              variant="outlined"
                            />
                            {!m.rfid_flag && (
                              <Chip
                                label="No RFID"
                                size="small"
                                color="warning"
                                variant="outlined"
                              />
                            )}
                            {m.quick_sell && (
                              <Chip
                                label="Quick Sell"
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            HSN: {m.hsn_code} &nbsp;|&nbsp; Stock:{" "}
                            {m.stock_percent || 0}% &nbsp;|&nbsp; Reorder:{" "}
                            {m.reorder_percent || 0}%
                          </Typography>
                        </Box>
                        <Box>
                          <IconButton
                            size="small"
                            onClick={() => openEditMetal(m)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => confirmDeleteMapping(m)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* ── Tab 2: Incentives ── */}
          {tabIndex === 2 && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  fontWeight={600}
                  color="text.secondary"
                  fontSize="0.85rem"
                >
                  Staff Incentive Configuration
                </Typography>
                <Button
                  size="small"
                  startIcon={<Add />}
                  variant="outlined"
                  onClick={openAddIncentive}
                  sx={{ borderRadius: 2 }}
                >
                  Add Incentive
                </Button>
              </Box>

              {incentives.length === 0 ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    textAlign: "center",
                    borderRadius: 2,
                    border: "1px dashed",
                    borderColor: "divider",
                  }}
                >
                  <Typography color="text.disabled" fontSize="0.85rem">
                    No incentives configured yet.
                  </Typography>
                </Paper>
              ) : (
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Metal</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Value</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {incentives.map((i) => (
                        <TableRow key={i.id}>
                          <TableCell>
                            {i.metalName ||
                              metalTypes.find(
                                (m) => m.id === Number(i.metal_type_id),
                              )?.name ||
                              "All Metals"}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={i.incentive_type.replace("_", " ")}
                              size="small"
                              color={
                                i.incentive_type === "PER_GM"
                                  ? "primary"
                                  : i.incentive_type === "PER_PCS"
                                  ? "secondary"
                                  : "default"
                              }
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography fontWeight={600}>
                              {i.incentive_type === "MRP_PERCENT"
                                ? `${i.incentive_value}%`
                                : `₹${i.incentive_value}`}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() => openEditIncentive(i)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => confirmDeleteIncentive(i)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveItemType}
            sx={{
              borderRadius: 2,
              background: "linear-gradient(135deg, #f59e0b, #d97706)",
              "&:hover": {
                background: "linear-gradient(135deg, #d97706, #b45309)",
              },
            }}
          >
            {editMode ? "Update" : "Create"} Item Type
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════════════
          METAL MAPPING DIALOG
      ═══════════════════════════════════════════════════ */}
      <Dialog
        open={metalDialogOpen}
        onClose={() => setMetalDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography fontWeight={700}>
            {metalEditId ? "Edit Metal Mapping" : "Add Metal Mapping"}
          </Typography>
          <IconButton size="small" onClick={() => setMetalDialogOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>

        <Tabs
          value={metalTabIdx}
          onChange={(_, v) => setMetalTabIdx(v)}
          sx={{ px: 3, borderBottom: "1px solid", borderColor: "divider" }}
        >
          <Tab label="Basic" />
          <Tab label="Stock & ABC" />
          <Tab label="Flags" />
        </Tabs>

        <DialogContent sx={{ pt: 3 }}>
          {/* ── Basic ── */}
          {metalTabIdx === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  size="small"
                  required
                  disabled={loadingMetals}
                >
                  <InputLabel>Metal Type</InputLabel>
                  <Select
                    label="Metal Type"
                    value={mappingForm.metal_type_id}
                    onChange={(e) =>
                      setMappingForm((p) => ({
                        ...p,
                        metal_type_id: e.target.value,
                      }))
                    }
                  >
                    {loadingMetals ? (
                      <MenuItem disabled>Loading metals...</MenuItem>
                    ) : (
                      metalTypes.map((m) => (
                        <MenuItem key={m.id} value={m.id}>
                          {m.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>HSN Code</InputLabel>
                  <Select
                    label="HSN Code"
                    value={mappingForm.hsn_code}
                    onChange={(e) =>
                      setMappingForm((p) => ({
                        ...p,
                        hsn_code: e.target.value,
                      }))
                    }
                  >
                    {MOCK_HSN.map((h) => (
                      <MenuItem key={h.code} value={h.code}>
                        {h.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>GST Type</InputLabel>
                  <Select
                    label="GST Type"
                    value={mappingForm.gst_type}
                    onChange={(e) =>
                      setMappingForm((p) => ({
                        ...p,
                        gst_type: e.target.value,
                      }))
                    }
                  >
                    {MOCK_GST.map((g) => (
                      <MenuItem key={g.id} value={g.id}>
                        {g.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>MC Type</InputLabel>
                  <Select
                    label="MC Type"
                    value={mappingForm.mc_type}
                    onChange={(e) =>
                      setMappingForm((p) => ({ ...p, mc_type: e.target.value }))
                    }
                  >
                    {MC_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Default Floor</InputLabel>
                  <Select
                    label="Default Floor"
                    value={mappingForm.default_floor_id}
                    onChange={(e) =>
                      setMappingForm((p) => ({
                        ...p,
                        default_floor_id: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {MOCK_FLOORS.map((f) => (
                      <MenuItem key={f.id} value={f.id}>
                        {f.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Barcode Template</InputLabel>
                  <Select
                    label="Barcode Template"
                    value={mappingForm.barcode_template_id}
                    onChange={(e) =>
                      setMappingForm((p) => ({
                        ...p,
                        barcode_template_id: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {MOCK_BARCODE_TEMPLATES.map((b) => (
                      <MenuItem key={b.id} value={b.id}>
                        {b.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Starting Stock No"
                  type="number"
                  value={mappingForm.tag_counter}
                  onChange={(e) =>
                    setMappingForm((p) => ({
                      ...p,
                      tag_counter: Number(e.target.value),
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Gross %"
                  type="number"
                  value={mappingForm.gross_percent}
                  onChange={(e) =>
                    setMappingForm((p) => ({
                      ...p,
                      gross_percent: Number(e.target.value),
                    }))
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Net %"
                  type="number"
                  value={mappingForm.net_percent}
                  onChange={(e) =>
                    setMappingForm((p) => ({
                      ...p,
                      net_percent: Number(e.target.value),
                    }))
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Op Wt"
                  type="number"
                  value={mappingForm.opening_stock}
                  onChange={(e) =>
                    setMappingForm((p) => ({
                      ...p,
                      opening_stock: Number(e.target.value),
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Op Rate"
                  type="number"
                  value={mappingForm.opening_rate}
                  onChange={(e) =>
                    setMappingForm((p) => ({
                      ...p,
                      opening_rate: Number(e.target.value),
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Opening 2 (Wt)"
                  type="number"
                  value={mappingForm.opening_stock2}
                  onChange={(e) =>
                    setMappingForm((p) => ({
                      ...p,
                      opening_stock2: Number(e.target.value),
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Op Rate 2"
                  type="number"
                  value={mappingForm.opening_rate2}
                  onChange={(e) =>
                    setMappingForm((p) => ({
                      ...p,
                      opening_rate2: Number(e.target.value),
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Grouping / Bin"
                  value={mappingForm.group_name}
                  onChange={(e) =>
                    setMappingForm((p) => ({
                      ...p,
                      group_name: e.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Group Prefix"
                  value={mappingForm.group_prefix}
                  onChange={(e) =>
                    setMappingForm((p) => ({
                      ...p,
                      group_prefix: e.target.value,
                    }))
                  }
                />
              </Grid>
            </Grid>
          )}

          {/* ── Stock & ABC ── */}
          {metalTabIdx === 1 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Item Stock %"
                  type="number"
                  value={mappingForm.stock_percent}
                  onChange={(e) =>
                    setMappingForm((p) => ({
                      ...p,
                      stock_percent: e.target.value,
                    }))
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                  helperText="Share of total store inventory"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Item Re-Order %"
                  type="number"
                  value={mappingForm.reorder_percent}
                  onChange={(e) =>
                    setMappingForm((p) => ({
                      ...p,
                      reorder_percent: e.target.value,
                    }))
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                  error={
                    Number(mappingForm.reorder_percent) >=
                      Number(mappingForm.stock_percent) &&
                    Number(mappingForm.stock_percent) > 0
                  }
                  helperText="Must be less than Stock %"
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    ROL ABC Report
                  </Typography>
                </Divider>
              </Grid>
              <Grid item xs={12}>
                <AbcFields mapping={mappingForm} setMapping={setMappingForm} />
              </Grid>
            </Grid>
          )}

          {/* ── Flags ── */}
          {metalTabIdx === 2 && (
            <Grid container spacing={1}>
              {[
                {
                  key: "rfid_flag",
                  label: "RFID Enabled",
                  hint: "Uncheck to disable RFID for this item-metal",
                },
                {
                  key: "quick_sell",
                  label: "Quick Sell",
                  hint: "Show in quick sale screen",
                },
                {
                  key: "size_wise_tag",
                  label: "Size-Wise Tagno",
                  hint: "Separate tag numbering per size",
                },
                {
                  key: "others_stock",
                  label: "Others Stock",
                  hint: "Include in other stock bucket",
                },
                {
                  key: "old_metal",
                  label: "Old Metal",
                  hint: "Marks this as old/used metal",
                },
              ].map(({ key, label, hint }) => (
                <Grid item xs={12} sm={6} key={key}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography fontSize="0.88rem" fontWeight={600}>
                        {label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {hint}
                      </Typography>
                    </Box>
                    <Checkbox
                      checked={!!mappingForm[key]}
                      onChange={(e) =>
                        setMappingForm((p) => ({
                          ...p,
                          [key]: e.target.checked,
                        }))
                      }
                    />
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setMetalDialogOpen(false)}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveMapping}
            sx={{
              borderRadius: 2,
              background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
              "&:hover": { background: "#1d4ed8" },
            }}
          >
            {metalEditId ? "Update" : "Add"} Metal
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════════════
          INCENTIVE DIALOG
      ═══════════════════════════════════════════════════ */}
      <Dialog
        open={incDialogOpen}
        onClose={() => setIncDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography fontWeight={700}>
            {incEditId ? "Edit Incentive" : "Add Incentive"}
          </Typography>
          <IconButton size="small" onClick={() => setIncDialogOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" disabled={loadingMetals}>
                <InputLabel>Metal (Optional)</InputLabel>
                <Select
                  label="Metal (Optional)"
                  value={incForm.metal_type_id}
                  onChange={(e) =>
                    setIncForm((p) => ({ ...p, metal_type_id: e.target.value }))
                  }
                >
                  <MenuItem value="">
                    <em>All Metals</em>
                  </MenuItem>
                  {loadingMetals ? (
                    <MenuItem disabled>Loading...</MenuItem>
                  ) : (
                    metalTypes.map((m) => (
                      <MenuItem key={m.id} value={m.id}>
                        {m.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Incentive Type</InputLabel>
                <Select
                  label="Incentive Type"
                  value={incForm.incentive_type}
                  onChange={(e) =>
                    setIncForm((p) => ({
                      ...p,
                      incentive_type: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="PER_GM">Per Gram</MenuItem>
                  <MenuItem value="PER_PCS">Per Piece</MenuItem>
                  <MenuItem value="MRP_PERCENT">MRP %</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label={
                  incForm.incentive_type === "MRP_PERCENT"
                    ? "MRP %"
                    : "Incentive Value (₹)"
                }
                value={incForm.incentive_value}
                onChange={(e) =>
                  setIncForm((p) => ({ ...p, incentive_value: e.target.value }))
                }
                InputProps={{
                  startAdornment:
                    incForm.incentive_type !== "MRP_PERCENT" ? (
                      <InputAdornment position="start">₹</InputAdornment>
                    ) : undefined,
                  endAdornment:
                    incForm.incentive_type === "MRP_PERCENT" ? (
                      <InputAdornment position="end">%</InputAdornment>
                    ) : undefined,
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setIncDialogOpen(false)}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveIncentive}
            sx={{
              borderRadius: 2,
              background: "linear-gradient(135deg, #10b981, #059669)",
              "&:hover": { background: "#059669" },
            }}
          >
            {incEditId ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════════════
          DELETE CONFIRM DIALOG
      ═══════════════════════════════════════════════════ */}
      <Dialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false })}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberRounded color="error" />
          <Typography fontWeight={700}>Confirm Delete</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>{deleteConfirm.label}</strong>? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setDeleteConfirm({ open: false })}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={executeDelete}
            sx={{ borderRadius: 2 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════════════
          CENTRED SNACKBAR
      ═══════════════════════════════════════════════════ */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={hide}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={hide}
          severity={snack.severity}
          variant="filled"
          sx={{ borderRadius: 2, fontWeight: 500 }}
          iconMapping={{
            success: <CheckCircleOutline fontSize="inherit" />,
            error: <WarningAmberRounded fontSize="inherit" />,
          }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ItemTypeMaster;
